import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/use-auth';
import { 
  useDivisionWorkflows, 
  useCreateDivisionWorkflow, 
  useUpdateDivisionWorkflow, 
  useDeleteDivisionWorkflow,
  useCloneDivisionWorkflow,
  useDivisionWorkflowExecutions
} from '../../hooks/use-division-workflows';
import { Card } from '../layout';
import { Button } from '../ui/button';
import { Input, Checkbox, Select } from '../ui/form';
import { Modal } from '../ui/modal';
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHead } from '../ui/table';
import { Badge } from '../ui/badge';
import { Tabs } from '../ui/tabs';
import { WorkflowStepsEditor } from '../workflow/workflow-steps-editor';
import { WorkflowImportModal } from '../workflow/workflow-import-modal';
import { WorkflowWizard } from '../workflow-wizard';
import { useDivision } from '../../hooks/use-divisions';
import { 
  WorkflowSummaryDto,
  WorkflowDetailResponse,
  CreateWorkflowRequest, 
  UpdateWorkflowRequest,
  CreateWorkflowStepDto
} from '../../types';

interface DivisionWorkflowManagementProps {
  divisionId: string;
  className?: string;
  readOnly?: boolean;
}

export function DivisionWorkflowManagement({ divisionId, className, readOnly = false }: DivisionWorkflowManagementProps) {
  const navigate = useNavigate();
  // Deleting a workflow is restricted to Admin/DivisionAdmin - WorkflowAdmin can create/edit/clone
  // but not permanently delete.
  const { isAdmin } = useAuth();
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowSummaryDto | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExecutionsModal, setShowExecutionsModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [deleteBlockedInfo, setDeleteBlockedInfo] = useState<{
    message: string;
    orders: Array<{ orderId: string; orderNumber: string; status: string }>;
  } | null>(null);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState<string | null>(null);

  // Ref to track search input for focus restoration
  const searchInputRef = useRef<HTMLInputElement>(null);
  const shouldRestoreFocus = useRef(false);

  // Filter, sort, and pagination state
  const [searchInput, setSearchInput] = useState(''); // Local state for input
  const [search, setSearch] = useState(''); // Debounced search value
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [matchingModeFilter, setMatchingModeFilter] = useState('');
  const [sortBy, setSortBy] = useState('CreatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // Reset to page 1 when search changes
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Build params object
  const params = {
    active: activeFilter,
    type: typeFilter || undefined,
    matchingMode: matchingModeFilter || undefined,
    search: search || undefined,
    sortBy,
    sortOrder,
    page,
    limit,
  };

  // Hooks for API operations
  const { data: workflowsResponse, isLoading, isFetching } = useDivisionWorkflows(divisionId, params);
  const createWorkflow = useCreateDivisionWorkflow(divisionId);
  const updateWorkflow = useUpdateDivisionWorkflow(divisionId);
  const deleteWorkflow = useDeleteDivisionWorkflow(divisionId);
  const cloneWorkflow = useCloneDivisionWorkflow(divisionId);

  const workflows = workflowsResponse?.items || [];
  const totalCount = workflowsResponse?.totalCount || 0;
  const totalPages = workflowsResponse?.totalPages || 0;
  const hasNextPage = workflowsResponse?.hasNextPage || false;
  const hasPreviousPage = workflowsResponse?.hasPreviousPage || false;

  // Restore focus to search input after data loads
  useEffect(() => {
    if (!isFetching && shouldRestoreFocus.current && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isFetching]);

  // Reset to page 1 when filters change
  const handleFilterChange = () => {
    setPage(1);
  };

  // Handle column sorting
  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleCreate = (request: CreateWorkflowRequest) => {
    createWorkflow.mutate(request, {
      onSuccess: () => {
        setShowCreateModal(false);
      },
    });
  };

  const handleDelete = (workflow: WorkflowSummaryDto) => {
    if (window.confirm(`Are you sure you want to delete "${workflow.name}"?`)) {
      deleteWorkflow.mutate(workflow.id, {
        onSuccess: () => {
          setSelectedWorkflow(null);
          setDeleteSuccessMessage(`Workflow "${workflow.name}" was deleted successfully.`);
          setTimeout(() => setDeleteSuccessMessage(null), 5000);
        },
        onError: (error: any) => {
          const apiError = error.response?.data;
          if (apiError?.errorCode === 'WORKFLOW_HAS_ACTIVE_EXECUTIONS') {
            setDeleteBlockedInfo({
              message: apiError.message,
              orders: apiError.details?.orders || [],
            });
            return;
          }

          let message = 'Failed to delete workflow';
          if (typeof apiError === 'string') {
            message = apiError;
          } else if (apiError?.message) {
            message = apiError.message;
          } else if (apiError?.detail) {
            message = apiError.detail;
          } else if (error.message) {
            message = error.message;
          }
          alert(message);
        },
      });
    }
  };

  const handleClone = (workflowId: string, cloneOptions: { name?: string; active?: boolean; includeMatchCriteria?: boolean }) => {
    cloneWorkflow.mutate({ workflowId, request: cloneOptions }, {
      onSuccess: (response) => {
        setShowCloneModal(false);
        setSelectedWorkflow(null);
        // Optionally navigate to the cloned workflow
        if (window.confirm('Workflow cloned successfully! Would you like to edit the cloned workflow now?')) {
          navigate(`/workflows/${divisionId}/${response.id}/edit`);
        }
      },
      onError: (error: any) => {
        let message = 'Failed to clone workflow';
        
        if (error.response?.data) {
          // Check different response formats (ASP.NET Core BadRequest can return different formats)
          if (typeof error.response.data === 'string') {
            message = error.response.data;
          } else if (error.response.data.detail) {
            // ProblemDetails format
            message = error.response.data.detail;
          } else if (error.response.data.title) {
            message = error.response.data.title;
          } else if (error.response.data.message) {
            message = error.response.data.message;
          }
        } else if (error.message) {
          message = error.message;
        }
        
        alert(message);
      }
    });
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-300 dark:bg-secondary-600 rounded w-1/4"></div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-300 dark:bg-secondary-600 rounded"></div>
              <div className="h-3 bg-gray-300 dark:bg-secondary-600 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Delete Success Toast */}
      {deleteSuccessMessage && (
        <div
          role="status"
          className="fixed top-20 right-4 z-50 max-w-md rounded-lg shadow-lg p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800"
        >
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-circle-check text-green-600 dark:text-green-400 mt-0.5"></i>
            <p className="flex-1 text-sm font-medium text-green-800 dark:text-green-300">{deleteSuccessMessage}</p>
            <button
              onClick={() => setDeleteSuccessMessage(null)}
              className="text-green-600 dark:text-green-400 hover:text-green-700"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Workflow Management</h2>
          <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">
            Create and manage approval workflows for this division
          </p>
        </div>
        <div className="flex gap-2">
          {!readOnly && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2"
                data-testid="btn-import-workflows"
              >
                <span>📥</span>
                Import CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/workflows/${divisionId}/new`)}
                className="flex items-center gap-2"
                data-testid="btn-use-wizard"
              >
                <span>🧙</span>
                Use Wizard
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2"
                data-testid="btn-quick-create"
              >
                <span>+</span>
                Quick Create
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search workflows..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => { shouldRestoreFocus.current = true; }}
              onBlur={() => { shouldRestoreFocus.current = false; }}
            />
          </div>

          {/* Active Filter */}
          <Select
            value={activeFilter === undefined ? '' : activeFilter.toString()}
            onChange={(e) => {
              const value = e.target.value;
              setActiveFilter(value === '' ? undefined : value === 'true');
              handleFilterChange();
            }}
          >
            <option value="">All Statuses</option>
            <option value="true">Active Only</option>
            <option value="false">Inactive Only</option>
          </Select>

          {/* Type Filter */}
          <Select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              handleFilterChange();
            }}
          >
            <option value="">All Types</option>
            <option value="purchase_order">Purchase Order</option>
            <option value="invoice">Invoice</option>
            <option value="expense">Expense</option>
          </Select>

          {/* Matching Mode Filter */}
          <Select
            value={matchingModeFilter}
            onChange={(e) => {
              setMatchingModeFilter(e.target.value);
              handleFilterChange();
            }}
          >
            <option value="">All Matching Modes</option>
            <option value="None">None</option>
            <option value="RoutingRules">Routing Rules</option>
          </Select>
        </div>

        {/* Sort and Display Controls */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-secondary-700">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 dark:text-secondary-400">Sort by:</span>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-auto"
            >
              <option value="CreatedAt">Created Date</option>
              <option value="UpdatedAt">Updated Date</option>
              <option value="Name">Name</option>
              <option value="Type">Type</option>
              <option value="Active">Status</option>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-secondary-400">Per page:</span>
            <Select
              value={limit.toString()}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="w-auto"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Results Summary */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-secondary-400">
          <span>
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount} workflows
          </span>
        </div>
      )}

      {/* Workflows List */}
      {workflows.length === 0 ? (
        <Card>
          <div className="p-8 text-center">
            <div className="text-gray-400 dark:text-secondary-500 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {search || activeFilter !== true || typeFilter || matchingModeFilter
                ? 'No workflows match your filters'
                : 'No workflows yet'}
            </h3>
            <p className="text-gray-600 dark:text-secondary-400 mb-4">
              {search || activeFilter !== true || typeFilter || matchingModeFilter
                ? 'Try adjusting your filters to see more results.'
                : 'Get started by creating your first approval workflow.'}
            </p>
            {!search && activeFilter === true && !typeFilter && !matchingModeFilter && !readOnly && (
              <Button onClick={() => setShowCreateModal(true)}>
                Create Your First Workflow
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  sortable
                  sortDirection={sortBy === 'Name' ? sortOrder : null}
                  onSort={() => handleSort('Name')}
                >
                  Name
                </TableHead>
                <TableHead 
                  sortable
                  sortDirection={sortBy === 'Type' ? sortOrder : null}
                  onSort={() => handleSort('Type')}
                >
                  Type
                </TableHead>
                <TableHead 
                  sortable
                  sortDirection={sortBy === 'Active' ? sortOrder : null}
                  onSort={() => handleSort('Active')}
                >
                  Status
                </TableHead>
                <TableHead>Workflow Steps</TableHead>
                <TableHead 
                  sortable
                  sortDirection={sortBy === 'CreatedAt' ? sortOrder : null}
                  onSort={() => handleSort('CreatedAt')}
                >
                  Created
                </TableHead>
                <TableHead 
                  sortable
                  sortDirection={sortBy === 'UpdatedAt' ? sortOrder : null}
                  onSort={() => handleSort('UpdatedAt')}
                >
                  Last Modified
                </TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workflows.map((workflow) => (
                <TableRow key={workflow.id} data-testid={`workflow-row-${workflow.id}`}>
                  <TableCell>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white" data-testid={`workflow-name-${workflow.id}`}>{workflow.name}</div>
                      <div className="text-sm text-gray-500 dark:text-secondary-400">{workflow.description}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="info" className="capitalize">
                      {workflow.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={workflow.active ? 'success' : 'default'}>
                      {workflow.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600 dark:text-secondary-400">
                      {workflow.steps?.length || 0} steps
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600 dark:text-secondary-400">
                      {new Date(workflow.createdAt).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600 dark:text-secondary-400">
                      {new Date(workflow.updatedAt).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/workflows/${divisionId}/${workflow.id}/view`)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/workflows/${divisionId}/${workflow.id}/edit`)}
                        data-testid={`btn-edit-${workflow.id}`}
                        style={readOnly ? { display: 'none' } : undefined}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/all-orders?workflowId=${workflow.id}&workflowName=${encodeURIComponent(workflow.name)}`)}
                        title="View all orders using this workflow"
                      >
                        Orders
                      </Button>
                      {!readOnly && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedWorkflow(workflow);
                            setShowCloneModal(true);
                          }}
                        >
                          Clone
                        </Button>
                      )}
                      {!readOnly && isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(workflow)}
                          className="text-red-600 dark:text-red-400 hover:text-red-700"
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-secondary-700">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={!hasPreviousPage}
                >
                  ← Previous
                </Button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={!hasNextPage}
                >
                  Next →
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Workflow Wizard */}
      <WorkflowWizard
        divisionId={divisionId}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(workflowId) => {
          setShowCreateModal(false);
          // Optionally navigate to the new workflow
          navigate(`/workflows/${divisionId}/${workflowId}/edit`);
        }}
      />

      {selectedWorkflow && (
        <CloneWorkflowModal
          isOpen={showCloneModal}
          workflow={selectedWorkflow}
          onClose={() => {
            setShowCloneModal(false);
            setSelectedWorkflow(null);
          }}
          onSubmit={(options) => handleClone(selectedWorkflow.id, options)}
          loading={cloneWorkflow.isPending}
        />
      )}

      <WorkflowImportModal
        divisionId={divisionId}
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => {
          // Workflow list will refresh via query invalidation; modal stays open to show results
        }}
      />

      <Modal
        isOpen={!!deleteBlockedInfo}
        onClose={() => setDeleteBlockedInfo(null)}
        title="Cannot Delete Workflow"
        data-testid="modal-delete-blocked"
      >
        {deleteBlockedInfo && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-secondary-300">{deleteBlockedInfo.message}</p>
            {deleteBlockedInfo.orders.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deleteBlockedInfo.orders.map((order) => (
                    <TableRow key={order.orderId}>
                      <TableCell>{order.orderNumber}</TableCell>
                      <TableCell>
                        <Badge variant="info">{order.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="flex justify-end">
              <Button onClick={() => setDeleteBlockedInfo(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Create Workflow Modal
interface CreateWorkflowModalProps {
  isOpen: boolean;
  divisionId: string;
  onClose: () => void;
  onSubmit: (request: CreateWorkflowRequest) => void;
  loading: boolean;
}

function CreateWorkflowModal({ isOpen, divisionId, onClose, onSubmit, loading }: CreateWorkflowModalProps) {
  const { data: divisionData } = useDivision(divisionId, !!divisionId);
  const [formData, setFormData] = useState<CreateWorkflowRequest>({
    name: '',
    description: '',
    type: 'approval',
    matchingMode: 'none',
    matchCriteria: { matchIdentifiers: [], executionIdentifiers: [] },
    workflowSteps: [],
    routingRules: [],
    settings: {
      autoEscalation: false,
      escalationHours: 24,
      notifications: {
        email: true,
        slack: false,
        sms: false,
      },
      timeoutAction: 'escalate' as const,
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleWorkflowStepsChange = useCallback((workflowSteps: CreateWorkflowStepDto[]) => {
    setFormData(prev => ({ ...prev, workflowSteps }));
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Workflow">
      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl p-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Basic Information</h3>
          <div className="space-y-4">
            <Input
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            
            <Input
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <Select
              label="Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <option value="approval">Approval Workflow</option>
              <option value="review">Review Workflow</option>
              <option value="notification">Notification Workflow</option>
            </Select>
          </div>
        </div>

        {/* Settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center">
              <Checkbox
                checked={formData.settings.autoEscalation}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, autoEscalation: e.target.checked }
                })}
              />
              <label className="ml-2 text-sm font-medium text-gray-700 dark:text-secondary-200">
                Auto Escalation
              </label>
            </div>
            
            <div className="flex items-center">
              <Checkbox
                checked={formData.settings.notifications.email}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { 
                    ...formData.settings, 
                    notifications: { ...formData.settings.notifications, email: e.target.checked }
                  }
                })}
              />
              <label className="ml-2 text-sm font-medium text-gray-700 dark:text-secondary-200">
                Email Notifications
              </label>
            </div>
          </div>
        </div>

        {/* Workflow Steps */}
        <div>
          <WorkflowStepsEditor
            divisionId={divisionId}
            workflowSteps={[]}
            onChange={handleWorkflowStepsChange}
            forceRequireExplicitSubmit={(divisionData?.settings?.requireExplicitSubmit ?? false) || (formData.settings.requireExplicitSubmit ?? false)}
          />
        </div>

        <div className="flex justify-end gap-2 pt-6 border-t border-gray-200 dark:border-secondary-700">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create Workflow'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}


// Edit Workflow Modal
interface EditWorkflowModalProps {
  isOpen: boolean;
  workflow: WorkflowSummaryDto;
  divisionId: string;
  onClose: () => void;
  onSubmit: (request: UpdateWorkflowRequest) => void;
  loading: boolean;
}

function EditWorkflowModal({ isOpen, workflow, divisionId, onClose, onSubmit, loading }: EditWorkflowModalProps) {
  const { data: divisionData } = useDivision(divisionId, !!divisionId);
  const [formData, setFormData] = useState<UpdateWorkflowRequest>({
    name: workflow.name,
    description: workflow.description,
    active: workflow.active,
    matchCriteria: workflow.matchCriteria || { matchIdentifiers: [], executionIdentifiers: [] },
    workflowSteps: workflow.steps || [],
    settings: workflow.settings || {
      autoEscalation: false,
      escalationHours: 24,
      notifications: {
        email: true,
        slack: false,
        sms: false,
      },
      timeoutAction: 'escalate' as const,
    },
  });

  const [activeTab, setActiveTab] = useState<'basic' | 'approval-levels' | 'settings'>('basic');

  // Reinitialize form data when workflow changes
  React.useEffect(() => {
    if (isOpen && workflow) {
      setFormData({
        name: workflow.name,
        description: workflow.description,
        active: workflow.active,
        matchCriteria: workflow.matchCriteria || { matchIdentifiers: [], executionIdentifiers: [] },
        workflowSteps: workflow.steps || [],
        settings: workflow.settings || {
          autoEscalation: false,
          escalationHours: 24,
          notifications: {
            email: true,
            slack: false,
            sms: false,
          },
          timeoutAction: 'escalate' as const,
        },
      });
      // Reset to first tab when opening
      setActiveTab('basic');
    }
  }, [isOpen, workflow]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleWorkflowStepsChange = useCallback((workflowSteps: CreateWorkflowStepDto[]) => {
    setFormData(prev => ({ ...prev, workflowSteps }));
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Edit Workflow: ${workflow.name}`}>
      <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl p-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-secondary-700">
          <nav className="-mb-px flex space-x-8">
            <button
              type="button"
              onClick={() => setActiveTab('basic')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'basic'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
              }`}
            >
              Basic Information
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('approval-levels')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'approval-levels'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
              }`}
            >
              Workflow Steps
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
              }`}
            >
              Settings
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Basic Information</h3>
              <Input
                label="Name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              
              <Input
                label="Description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />

              <div className="flex items-center">
                <Checkbox
                  checked={formData.active ?? true}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                <label className="ml-2 text-sm font-medium text-gray-700 dark:text-secondary-200">
                  Active
                </label>
              </div>
            </div>
          )}

          {/* Workflow Steps Tab */}
          {activeTab === 'approval-levels' && (
            <div>
              <WorkflowStepsEditor
                divisionId={divisionId}
                workflowSteps={workflow.steps || []}
                onChange={handleWorkflowStepsChange}
                forceRequireExplicitSubmit={(divisionData?.settings?.requireExplicitSubmit ?? false) || (formData.settings?.requireExplicitSubmit ?? false)}
              />
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Workflow Settings</h3>
              
              <div className="flex items-center">
                <Checkbox
                  checked={formData.settings?.autoEscalation ?? false}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { 
                      ...formData.settings!, 
                      autoEscalation: e.target.checked 
                    }
                  })}
                />
                <label className="ml-2 text-sm font-medium text-gray-700 dark:text-secondary-200">
                  Auto Escalation
                </label>
              </div>

              <Input
                label="Escalation Hours"
                type="number"
                value={(formData.settings?.escalationHours ?? 24).toString()}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { 
                    ...formData.settings!, 
                    escalationHours: parseInt(e.target.value) || 24
                  }
                })}
              />

              <Input
                label="Pause Timeout (hours)"
                type="number"
                min="1"
                value={formData.settings?.pauseTimeoutHours?.toString() ?? ''}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { 
                    ...formData.settings!, 
                    pauseTimeoutHours: e.target.value ? parseInt(e.target.value) : undefined
                  }
                })}
                placeholder="Leave empty to use division default"
              />
              <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                Override the division's default pause timeout for this workflow. Leave empty to use the division default.
              </p>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200">Notifications</label>
                
                <div className="flex items-center">
                  <Checkbox
                    checked={formData.settings?.notifications?.email ?? true}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: { 
                        ...formData.settings!, 
                        notifications: {
                          ...formData.settings!.notifications,
                          email: e.target.checked
                        }
                      }
                    })}
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700 dark:text-secondary-200">
                    Email Notifications
                  </label>
                </div>

                <div className="flex items-center">
                  <Checkbox
                    checked={formData.settings?.notifications?.slack ?? false}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: { 
                        ...formData.settings!, 
                        notifications: {
                          ...formData.settings!.notifications,
                          slack: e.target.checked
                        }
                      }
                    })}
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700 dark:text-secondary-200">
                    Slack Notifications
                  </label>
                </div>

                <div className="flex items-center">
                  <Checkbox
                    checked={formData.settings?.notifications?.sms ?? false}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: { 
                        ...formData.settings!, 
                        notifications: {
                          ...formData.settings!.notifications,
                          sms: e.target.checked
                        }
                      }
                    })}
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700 dark:text-secondary-200">
                    SMS Notifications
                  </label>
                </div>
              </div>

              <Select
                label="Timeout Action"
                value={formData.settings?.timeoutAction ?? 'escalate'}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { 
                    ...formData.settings!, 
                    timeoutAction: e.target.value as 'escalate' | 'approve' | 'reject'
                  }
                })}
              >
                <option value="escalate">Escalate</option>
                <option value="approve">Auto Approve</option>
                <option value="reject">Auto Reject</option>
              </Select>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-6 border-t border-gray-200 dark:border-secondary-700">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Clone Workflow Modal
interface CloneWorkflowModalProps {
  isOpen: boolean;
  workflow: WorkflowSummaryDto;
  onClose: () => void;
  onSubmit: (options: { name?: string; active?: boolean; includeMatchCriteria?: boolean }) => void;
  loading: boolean;
}

function CloneWorkflowModal({ isOpen, workflow, onClose, onSubmit, loading }: CloneWorkflowModalProps) {
  const [name, setName] = useState(`${workflow.name} (Copy)`);
  const [active, setActive] = useState(false);
  const [includeMatchCriteria, setIncludeMatchCriteria] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: name.trim() || undefined,
      active,
      includeMatchCriteria
    });
  };

  const handleClose = () => {
    setName(`${workflow.name} (Copy)`);
    setActive(false);
    setIncludeMatchCriteria(true);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Clone Workflow: ${workflow.name}`}>
      <form onSubmit={handleSubmit} className="space-y-6 max-w-lg p-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Clone Configuration</h3>
          <p className="text-sm text-gray-600 dark:text-secondary-400 mb-4">
            Create a copy of this workflow with the specified settings. All workflow steps and configurations will be duplicated.
          </p>
          
          <div className="space-y-4">
            <Input
              label="New Workflow Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Enter a name for the cloned workflow"
            />

            <div className="space-y-3">
              <Checkbox
                label="Start workflow as active"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              
              <Checkbox
                label="Include match criteria (identifiers & routing rules)"
                checked={includeMatchCriteria}
                onChange={(e) => setIncludeMatchCriteria(e.target.checked)}
              />
            </div>

            {active && includeMatchCriteria && (
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-yellow-800">
                    <strong>Warning:</strong> Cloning with both active status and match criteria may create duplicate routing rules if the source workflow is also active. Consider cloning as inactive first, then editing the routing rules before activating.
                  </div>
                </div>
              </div>
            )}

            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <div className="text-sm text-blue-800">
                <strong>Tip:</strong> The cloned workflow will include all steps, approvers, and settings. 
                You can edit it after cloning to make any necessary adjustments.
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-6 border-t border-gray-200 dark:border-secondary-700">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading || !name.trim()}>
            {loading ? 'Cloning...' : 'Clone Workflow'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
